import api from "@/api/v1-jwt/config/api";
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { createErrorHandler } from "@/utils/errorHandler";
import { PaymentMethod, UpdatePaymentMethodPayload } from "./types";
import paymentMethodQueryKeys from "./query-keys";

const updatePaymentMethod = async (
  payload: UpdatePaymentMethodPayload
): Promise<PaymentMethod> => {
  const url = `/platform/payment_methods/plaid`;
  const response = await api.patch(url, payload);
  return response.data?.data;
};

type UpdatePaymentMethodMutationOptions = UseMutationOptions<
  PaymentMethod,
  Error,
  UpdatePaymentMethodPayload
>;

export const useUpdatePaymentMethodOptions = (
  options?: UpdatePaymentMethodMutationOptions
): UpdatePaymentMethodMutationOptions => {
  const queryClient = useQueryClient();
  const onSettled = options?.onSettled;
  const defaultOptions: UpdatePaymentMethodMutationOptions = {
    mutationFn: updatePaymentMethod,
    onSettled: (...params) => {
      queryClient.invalidateQueries(paymentMethodQueryKeys.all);
      if (onSettled) {
        onSettled(...params);
      }
    },
    onError: createErrorHandler(),
  };
  return { ...defaultOptions, ...options };
};

export const useUpdatePaymentMethod = (
  options?: UpdatePaymentMethodMutationOptions
) => {
  const mutationOptions = useUpdatePaymentMethodOptions(options);
  return useMutation<PaymentMethod, Error, UpdatePaymentMethodPayload>(
    mutationOptions
  );
};

export default useUpdatePaymentMethod;
